import React, { useState, useEffect, useRef } from 'react';
import './DropdownMenu.css';

interface DropdownItem {
  id: string;
  label: string;
  onClick: () => void;
  type?: 'team' | 'project';
  icon?: string;
  iconPosition?: 'left' | 'right';
  className?: string;
}

interface DropdownMenuProps {
  isOpen: boolean;
  items: DropdownItem[];
  position?: 'left' | 'right';
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({ 
  isOpen, 
  items, 
  position = 'left' 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      // When closing, wait for animation to complete before removing from DOM
      const menu = menuRef.current;
      if (menu) {
        menu.style.animation = 'dropdownClose 0.12s ease-in forwards';
        const timer = setTimeout(() => {
          setIsVisible(false);
        }, 120); // Match animation duration
        return () => clearTimeout(timer);
      }
    }
  }, [isOpen]);
  
  if (!isVisible) return null;
  
  // Check if we need to group items (if any items have team/project types)
  const hasGroupedItems = items.some(item => item.type === 'team' || item.type === 'project');

  if (!hasGroupedItems) {
    // Render flat list for profile menu
    return (
      <div 
        ref={menuRef}
        className={`dropdown-menu ${position === 'right' ? 'dropdown-menu-right' : ''}`}
      >
        <div className="dropdown-section">
          {items.map((item) => (
            <button
              key={item.id}
              className={`dropdown-item ${item.className || ''}`}
              onClick={item.onClick}
            >
              {item.icon && item.iconPosition !== 'right' && (
                <img 
                  src={item.icon} 
                  alt="" 
                  className={`dropdown-item-icon dropdown-item-icon-left`}
                />
              )}
              {item.label}
              {item.icon && item.iconPosition === 'right' && (
                <img 
                  src={item.icon} 
                  alt="" 
                  className={`dropdown-item-icon dropdown-item-icon-right`}
                />
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Group items by team for team/project menu
  const groupedItems = items.reduce<{ team: DropdownItem | null; items: DropdownItem[] }[]>((acc, item) => {
    if (item.type === 'team') {
      acc.push({ team: item, items: [] });
    } else if (acc.length > 0) {
      acc[acc.length - 1].items.push(item);
    }
    return acc;
  }, []);
  
  return (
    <div 
      ref={menuRef}
      className={`dropdown-menu ${position === 'right' ? 'dropdown-menu-right' : ''}`}
    >
      {groupedItems.map(({ team, items }) => (
        <div key={team?.id} className="dropdown-section">
          <button
            className={`dropdown-item team-item`}
            onClick={team?.onClick}
          >
            {team?.label}
          </button>
          {items.map((item) => (
            <button
              key={item.id}
              className={`dropdown-item`}
              onClick={item.onClick}
            >
              {item.label}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
};

export default DropdownMenu; 